import { useTranslation } from "react-i18next";
import type { HeatmapCell } from "@/hooks/useReportsHeatmap";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";

interface AppointmentsHeatmapProps {
  data: HeatmapCell[];
  isLoading: boolean;
}

const SHIFTS = ["morning", "afternoon", "night"] as const;
const DAYS = [0, 1, 2, 3, 4, 5, 6]; // 0=Sun..6=Sat

function heatColor(value: number, max: number): string {
  if (max === 0) return "hsl(var(--muted))";
  const ratio = value / max;
  if (ratio === 0) return "hsl(var(--muted) / 0.3)";
  if (ratio < 0.25) return "hsl(38 92% 92%)";
  if (ratio < 0.5) return "hsl(38 92% 78%)";
  if (ratio < 0.75) return "hsl(28 85% 62%)";
  return "hsl(17 80% 50%)";
}

export function AppointmentsHeatmap({ data, isLoading }: AppointmentsHeatmapProps) {
  const { t, i18n } = useTranslation("reports");

  const dayLabels =
    i18n.language === "pt"
      ? ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"]
      : ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  const shiftLabels: Record<string, string> = {
    morning: t("heatmapMorning"),
    afternoon: t("heatmapAfternoon"),
    night: t("heatmapNight"),
  };

  // Build lookup map
  const lookup: Record<string, number> = {};
  let maxVal = 0;
  let peakDay = -1;
  let peakShift = "";
  data.forEach((cell) => {
    const key = `${cell.day_of_week}-${cell.shift}`;
    lookup[key] = cell.count;
    if (cell.count > maxVal) {
      maxVal = cell.count;
      peakDay = cell.day_of_week;
      peakShift = cell.shift;
    }
  });

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">{t("heatmapTitle")}</CardTitle>
        <CardDescription>{t("heatmapDesc")}</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-xs border-separate border-spacing-1">
            <thead>
              <tr>
                <th className="text-left font-medium text-muted-foreground pr-2 pb-1" />
                {SHIFTS.map((shift) => (
                  <th
                    key={shift}
                    className="text-center font-medium text-muted-foreground pb-1 min-w-[60px] md:min-w-[120px]"
                  >
                    {shiftLabels[shift]}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {DAYS.map((dow) => (
                <tr key={dow}>
                  <td className="text-foreground font-medium pr-2 py-1 whitespace-nowrap">
                    {dayLabels[dow]}
                  </td>
                  {SHIFTS.map((shift) => {
                    const key = `${dow}-${shift}`;
                    const val = lookup[key] ?? 0;
                    const isPeak = dow === peakDay && shift === peakShift && val > 0;
                    return (
                      <td key={shift} className="text-center py-1">
                        <div
                          className="mx-auto rounded-xl flex items-center justify-center h-8 w-full font-semibold text-xs transition-colors"
                          style={{ backgroundColor: isLoading ? "hsl(var(--muted))" : heatColor(val, maxVal) }}
                          title={`${dayLabels[dow]} · ${shiftLabels[shift]}: ${val}`}
                        >
                          {isLoading || val === 0 ? (
                            <span className="text-muted-foreground/50">–</span>
                          ) : (
                            <span className={isPeak ? "text-white" : "text-foreground/80"}>
                              {val}
                            </span>
                          )}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
          {/* Legend */}
          <div className="flex items-center gap-2 mt-3 justify-end">
            <span className="text-xs text-foreground">{t("heatmapLess")}</span>
            {["hsl(var(--muted) / 0.3)", "hsl(38 92% 92%)", "hsl(38 92% 78%)", "hsl(28 85% 62%)", "hsl(17 80% 50%)"].map(
              (color, i) => (
                <div
                  key={i}
                  className="h-4 w-4 rounded-full"
                  style={{ backgroundColor: isLoading ? "hsl(var(--muted))" : color }}
                />
              )
            )}
            <span className="text-xs text-foreground">{t("heatmapMore")}</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
